// Legacy map class. Delete this once the map improvements are finalized: https://jira.evbhome.com/browse/EB-81434
import React from 'react';
import PropTypes from 'prop-types';
import supercluster from 'supercluster';

import InteractiveMap from 'eventbrite_design_system/interactiveMap/InteractiveMap';
import withTooltip from 'eventbrite_design_system/tooltip/withTooltip';
import gettext from 'js-utils/gettext';

import InteractiveMapMarker from './interactiveMapMarker/InteractiveMapMarker';
import {GOOGLE_MAPS_CLIENT_KEY} from '../../constants';

import {MAP_TYPE_LOCAL, LOCAL_MAP_OPTIONS} from 'eventbrite_design_system/interactiveMap/constants';
import MapPinChunkySvg from 'eventbrite_design_system/iconography/icons/MapPinChunky';

import './searchMap.scss';

//TEMPORARY: Hard coded far out zoom level
//to guarantee all events are
//displayed on the map
// TODO: Come up with better way to determine bounds
//on the map
const DEFAULT_ZOOM_VALUE = 10;

// Max zoom is set to slightly less than the default max,
// which lets you get so close that the map is basically useless.
// Defining it in JS is also useful because we can use it to split up cluster
// names when the user can no longer zoom in any longer
const MAX_ZOOM = 16;

// Override some of the defaults from EDS
const SEARCH_MAP_OPTIONS = {
    ...LOCAL_MAP_OPTIONS,
    disableDefaultUI: false,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    maxZoom: MAX_ZOOM,
    minZoom: 3,
};

// Rough implementation to provide some interaction
// Tooltip component was not made for this in mind
// and has specific requirements around width/etc.
// TODO: Will require design input
const InteractiveMapMarkerWithTooltip = withTooltip(InteractiveMapMarker);

const MapMarker = ({label, name}) => (
    <InteractiveMapMarkerWithTooltip
        type={<MapPinChunkySvg />}
        color="ui-orange"
        tooltipId={`tooltip-${label}`}
        tooltipText={(
            <div style={{minWidth: '75px'}}>
                <div className="eds-text-weight--heavy eds-text--center">
                    {name}
                </div>
            </div>
        )}
    />
);

const _calculateAverageCenter = (clusters, defaultCenter) => {
    let latSum = 0;

    let lonSum = 0;

    clusters.forEach(({geometry}) => {
        /* Rough approximation of center point by averaging incoming
        events lat lon values */
        const lng = geometry.coordinates[0];
        const lat = geometry.coordinates[1];

        latSum += parseFloat(lat);
        lonSum += parseFloat(lng);
    });

    let center = {lat: latSum / clusters.length, lng: lonSum / clusters.length};

    if (!latSum || !lonSum) {
        center = defaultCenter;
    }

    return center;
};

const _convertEventsToGeoJSON = (events) => (
    // map events to objects that meet GeoJSON spec
    events.map(({name, latitude, longitude, id}) => ({
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [longitude, latitude],
        },
        properties: {
            name,
            id,
        },
    }))
);

export default class SearchMap extends React.PureComponent {
    static propTypes = {
        events: PropTypes.arrayOf(PropTypes.object),
        height: PropTypes.number,
        width: PropTypes.string,
        countryCode: PropTypes.string,
        googleMapLanguage: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
    }

    static defaultProps = {
        events: [],
        height: '400px',
        width: '100%',
    }

    constructor(props) {
        super(props);

        this.clusterIndex = supercluster({radius: 60, maxZoom: MAX_ZOOM});

        this.clusterIndex.load(_convertEventsToGeoJSON(props.events));

        const zoom = DEFAULT_ZOOM_VALUE;

        let center = {lat: props.latitude || 0, lng: props.longitude || 0};

        if (!props.latitude || !props.longitude) {
            center = _calculateAverageCenter(this.clusterIndex.getClusters([-180, -90, 180, 90], zoom), center);
        }

        this.state = {
            zoom,
            bounds: {
                // Note that by the time the user sees anything,
                // these will already be set to the correct bounds
                // because _handleBoundsChange gets automatically
                // called once by the library during the initial mount
                ne: {lat: 90, lng: 180},
                nw: {lat: 90, lng: -180},
                se: {lat: -90, lng: 180},
                sw: {lat: -90, lng: -180},
            },
            center,
        };
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(newProps) {
        let {center} = this.state;

        this.clusterIndex.load(_convertEventsToGeoJSON(newProps.events));

        if (!this.props.events.length && newProps.events.length) {
            // Check to make sure the previous props received were empty,
            // because this component receives an empty events array a few times
            // during the wait time for a search to complete.
            // We don't want to keep setting the center during that time
            // because it causes the map to jump around during the
            // old events array -> empty array -> new events array lifecycle

            const {zoom} = this.state;

            // If average center calculation fails, just use the current center
            center = _calculateAverageCenter(this.clusterIndex.getClusters([-180, -90, 180, 90], zoom)) || center;
        }

        this.setState({center});
    }

    _handleBoundsChange = ({zoom, bounds}) => {
        this.setState({zoom, bounds});
    }

    //Doesn't look like this handler is wired
    //up to anything on the InteractiveMap Component
    _handleMapMarkerClick = () => {
        //do nothing for now
    }

    _getEventMarkers = () => {
        // Turns an array of single points and clusters into an array of map markers.
        // Clusters represent groups of events that are very close to each other on the map,
        // which is determined by pixel radius.
        const {bounds, zoom} = this.state;
        const clusters = this.clusterIndex.getClusters([bounds.sw.lng, bounds.sw.lat, bounds.se.lng, bounds.ne.lat], zoom);

        return clusters.map(({geometry, properties}) => {
            const lng = geometry.coordinates[0];
            const lat = geometry.coordinates[1];

            let markerProps = {
                lat,
                lng,
                label: properties.id,
                name: properties.name,
            };

            if (properties && properties.cluster) {
                let clusterName = gettext('Zoom to see %(eventCount)s events', {eventCount: properties.point_count});

                if (properties.point_count === 2 || zoom === MAX_ZOOM) {
                    // In some cases, for example when points share the same address (like when they're at the same hotel),
                    // the clusters will never be split up into single points. Therefore, we need some logic to
                    // ensure that the user can still see the names.
                    const clusterChildren = this.clusterIndex.getLeaves(properties.cluster_id);

                    clusterName = clusterChildren.map(({properties: childProperties}) => childProperties.name).join(' & ');
                }

                markerProps = {
                    ...markerProps,
                    label: properties.id,
                    name: clusterName,
                };
            }

            return (
                <MapMarker
                    {...markerProps}
                    key={properties.id || `cluster-${properties.cluster_id}`}
                />
            );
        });
    };

    render() {
        const {
            height,
            width,
            countryCode,
            googleMapLanguage,
            events,
        } = this.props;

        let markers;

        if (events.length) {
            markers = this._getEventMarkers();
        }

        return (
            <section className="search-map" data-spec="search-map">
                <InteractiveMap
                    googleMapsClient={GOOGLE_MAPS_CLIENT_KEY}
                    height={height}
                    width={width}
                    language={googleMapLanguage}
                    region={countryCode}
                    mapType={MAP_TYPE_LOCAL}
                    center={this.state.center}
                    zoom={DEFAULT_ZOOM_VALUE}
                    onChange={this._handleBoundsChange}
                    options={SEARCH_MAP_OPTIONS}
                >
                    {markers}
                </InteractiveMap>
            </section>
        );
    }
}
